import type { ReactNode } from "react";
import { styled, useMedia, View, YStack } from "tamagui";
import type { Status } from "../../shared-types/status";
import { type ShadowVariant, shadow as shadowVariants } from "../../themes/token-shadow";
import { Button, type ButtonProps } from "../Button";
import { Icon, type IconComponent } from "../Icon";
import { webPointerProps } from "../interactive/styles";
import { useInteractiveElement } from "../interactive/useInteractiveElement";
import { pressVariants } from "../Motion/motion";
import { RowLayout } from "../Row/RowLayout";
import { Body2, Subtitle1, Title5 } from "../Typography";
const alertBarStatusTokens = {
  neutral: {
    bg: "$surface0",
    bgHover: "$surface0Hover",
    border: "$borderDefault",
    text: "$textDefault",
    icon: "$iconDefault"
  },
  positive: {
    bg: "$bgPositive",
    bgHover: "$bgPositiveHover",
    border: "$borderPositive",
    text: "$textPositiveStrong",
    icon: "$iconPositive"
  },
  negative: {
    bg: "$bgNegative",
    bgHover: "$bgNegativeHover",
    border: "$borderNegative",
    text: "$textNegativeStrong",
    icon: "$iconNegative"
  },
  warning: {
    bg: "$bgWarning",
    bgHover: "$bgWarningHover",
    border: "$borderWarning",
    text: "$textWarningStrong",
    icon: "$iconWarning"
  },
  info: {
    bg: "$bgAccent",
    bgHover: "$bgAccentHover",
    border: "$borderAccent",
    text: "$textAccentStrong",
    icon: "$iconAccent"
  }
} as const satisfies Record<Status, {
  bg: string;
  bgHover: string;
  border: string;
  text: string;
  icon: string;
}>;

// A YStack so the single RowLayout child stretches to full width; slot gaps
// live in RowLayout. Prominent's extra desktop gap is margin on the slots.
const StyledAlertBar = styled(YStack, {
  name: "AlertBar",
  borderWidth: 1,
  borderRadius: "$br2",
  padding: "$sp1.5",
  variants: {
    status: {
      neutral: {
        backgroundColor: alertBarStatusTokens.neutral.bg,
        borderColor: alertBarStatusTokens.neutral.border
      },
      positive: {
        backgroundColor: alertBarStatusTokens.positive.bg,
        borderColor: alertBarStatusTokens.positive.border
      },
      negative: {
        backgroundColor: alertBarStatusTokens.negative.bg,
        borderColor: alertBarStatusTokens.negative.border
      },
      warning: {
        backgroundColor: alertBarStatusTokens.warning.bg,
        borderColor: alertBarStatusTokens.warning.border
      },
      info: {
        backgroundColor: alertBarStatusTokens.info.bg,
        borderColor: alertBarStatusTokens.info.border
      }
    },
    shadow: shadowVariants,
    bordered: {
      true: {},
      false: {
        borderWidth: 0
      }
    },
    prominent: {
      true: {
        padding: "$sp2"
      },
      false: {}
    }
  } as const,
  defaultVariants: {
    status: "neutral"
  }
});
type BaseProps = {
  status?: Status;
  /** Elevation for a floating bar; omit for a flat inline alert. */
  shadow?: ShadowVariant;
  /** Default true; false gives a flat fill with no outline (e.g. home banners). */
  bordered?: boolean;
  /** Hero-scale spacing, and the title steps up to `Title5` on desktop. */
  prominent?: boolean;
  /** Makes the whole bar tappable (with hover/press feedback). */
  onPress?: () => void;
  /** Only for a failure the user just caused or is waiting on; a bar that loads
   *  with the screen stays silent. Good news goes in a `LiveRegion` mounted
   *  before it instead. */
  interrupt?: boolean;
  testID?: string;
};

/** A bolder `Subtitle1` heading above the message; both use the strong status
 *  color. At least one of `title` / `message` must be provided. */
type ContentProps = {
  title: string;
  message?: string;
} | {
  title?: string;
  message: string;
};

/** `icon` renders token-colored per status; `leading` is a fully open slot
 *  (an Avatar, an image, anything) — the caller owns its styling. */
type LeadingProps = {
  icon?: IconComponent;
  leading?: never;
} | {
  icon?: never;
  leading?: ReactNode;
};

/** A trailing button and a trailing plain icon are mutually exclusive. */
type TrailingProps = {
  button?: ButtonProps;
  trailingIcon?: never;
} | {
  button?: never;
  trailingIcon?: IconComponent;
};
export type AlertBarProps = BaseProps & ContentProps & LeadingProps & TrailingProps;
export const AlertBar = ({
  title,
  message,
  icon,
  leading,
  button,
  trailingIcon,
  status = "neutral",
  shadow,
  bordered = true,
  prominent = false,
  onPress,
  interrupt = false,
  testID
}: AlertBarProps) => {
  const tokens = alertBarStatusTokens[status];
  const media = useMedia();
  const Title = prominent && media.gtXs ? Title5 : Subtitle1;
  const {
    isInteractive,
    renderTag,
    interactiveProps
  } = useInteractiveElement({
    onPress
  });
  const leadingNode = icon ? <Icon icon={icon} size="$iconLarge" color={tokens.icon} /> : leading;
  const trailing = button ? <Button {...button} /> : trailingIcon ? <Icon icon={trailingIcon} size="$iconLarge" color={tokens.icon} /> : undefined;
  const messageNode = message ? <Body2 color={tokens.text} flexShrink={1}>
      {message}
    </Body2> : undefined;

  // Status-colored hover/press can't live in a boolean variant, so it joins
  // the shared behavior styles at JSX time (the SelectionCard pattern).
  const pressableProps = isInteractive ? {
    ...webPointerProps,
    transition: "press" as const,
    hoverStyle: {
      backgroundColor: tokens.bgHover
    },
    pressStyle: {
      backgroundColor: tokens.bgHover,
      ...pressVariants.default.pressStyle
    }
  } : {};
  return <StyledAlertBar
  // Re-key on render-tag transitions — toggling `transition` mid-life
  // shifts Tamagui's hook order (see Card).
  key={renderTag ?? "static"} role={isInteractive ? "button" : interrupt ? "alert" : undefined} status={status} shadow={shadow} bordered={bordered} prominent={prominent} testID={testID} {...pressableProps} {...renderTag ? {
    render: renderTag
  } : {}} {...interactiveProps} data-sentry-element="StyledAlertBar" data-sentry-component="AlertBar" data-sentry-source-file="AlertBar.tsx">
      <RowLayout leading={leadingNode && prominent ? <View $gtXs={{
      marginRight: "$sp0.5"
    }}>{leadingNode}</View> : leadingNode} primary={title ? <Title color={tokens.text} flexShrink={1}>
              {title}
            </Title> : messageNode} secondary={title ? messageNode : undefined} rowTrailing={trailing && prominent ? <View $gtXs={{
      marginLeft: "$sp0.5"
    }}>{trailing}</View> : trailing} data-sentry-element="RowLayout" data-sentry-source-file="AlertBar.tsx" />
    </StyledAlertBar>;
};